import Analytics from '@ublend-npm/aula-analytics';
import redirectToGeneral from '$utils/redirectToGeneral';
import redirectToSpace from '$utils/redirectToSpace';
import { init as initAnalytics } from '$utils/analytics';
import { CHANGE_SPACE } from '$variables/analyticsEvents';

import {
  INITIALIZE_APP_STARTED,
  INITIALIZE_APP_SUCCESS,
  INITIALIZE_APP_FAILED,
  FETCH_INSTITUTION_SUCCESS,
  FETCH_CURRENT_USER_SUCCESS,
  FETCH_ACCESS_TOKEN_STARTED,
  FETCH_ACCESS_TOKEN_SUCCESS,
  FETCH_ACCESS_TOKEN_FAILED,
  FETCH_USER_INFO_ON_SPACES_SUCCESS,
  CHANGE_CURRENT_SPACE,
} from './core.types';

import {
  fetchInstitution,
  fetchCurrentUser,
  fetchUserInfoOnSpaces,
  fetchSpaceInfo,
  fetchAccessToken,
} from './core.effects';

import coreSelectors from './core.selectors';

const fetchNewAccessToken = async ({ dispatch, spaceId }) => {
  dispatch({
    type: FETCH_ACCESS_TOKEN_STARTED,
    payload: { spaceId },
  });

  try {
    // Get new access token
    const accessToken = await fetchAccessToken({
      spaceId,
    });

    dispatch({
      type: FETCH_ACCESS_TOKEN_SUCCESS,
      payload: {
        accessToken,
        spaceId,
      },
    });
  } catch (error) {
    dispatch({
      type: FETCH_ACCESS_TOKEN_FAILED,
      payload: {
        error,
        spaceId,
      },
    });
  }
};

// fetch accessToken for specified space if it's expired or not already in state
const fetchNewTokenIfNeeded =
  ({ spaceId }) =>
  async (dispatch, getState) => {
    const currentState = getState();
    const { token, expiry, isFetching } =
      coreSelectors.getAccessTokenInfo(currentState, spaceId) || {};

    // check if accessToken for spaceId exists or needs refreshing
    const needsRefresh = !token || Date.now() > expiry;

    // do refresh if needed and we're not already refreshing
    if (needsRefresh && !isFetching) {
      await fetchNewAccessToken({
        dispatch,
        spaceId,
      });
    }
  };

const initializeApp = (spaceId) => async (dispatch) => {
  dispatch({
    type: INITIALIZE_APP_STARTED,
  });

  try {
    // Fetch the institution
    const { general, features } = await fetchInstitution();

    dispatch({
      type: FETCH_INSTITUTION_SUCCESS,
      payload: {
        general,
        features,
      },
    });

    // Get the current user and transform it to JSON
    const user = await fetchCurrentUser();

    if (!user) {
      return redirectToGeneral({ returnAfterLogin: true });
    }

    dispatch({
      type: FETCH_CURRENT_USER_SUCCESS,
      payload: {
        user,
      },
    });

    // Get spaces
    let userInfoOnSpaces = await fetchUserInfoOnSpaces({
      user,
    });

    let space = userInfoOnSpaces.find((info) => info.id === spaceId);

    // Use is not a member of the space but has staff user role
    const userIsStaffOnly = user.userRoles.includes('staff') && !space;
    if (userIsStaffOnly && spaceId) {
      const spaceInfo = await fetchSpaceInfo({ spaceId });

      // If user is staff only, the space being accessed will not be returned when
      // fetching the spaces from the user, so we need to manually add the space.
      space = {
        ...spaceInfo,
        isStaffOnly: true,
      };
      userInfoOnSpaces = [...userInfoOnSpaces, space];
    }

    if (!space) {
      if (!userInfoOnSpaces.length) {
        return redirectToGeneral({ returnAfterLogin: false });
      }

      const newSpaceId = userInfoOnSpaces[0].id;
      return redirectToSpace(newSpaceId);
    }

    dispatch({
      type: FETCH_USER_INFO_ON_SPACES_SUCCESS,
      payload: {
        userInfoOnSpaces,
        spaceId,
      },
    });
    await fetchNewAccessToken({ dispatch, spaceId });

    // setup analytics
    await initAnalytics({
      institution: general.institution,
      user,
      spaceId: space.id,
      isInstructor: space.role === 'educator',
      accessingAsStaff: space.isStaffOnly,
    });
  } catch (error) {
    dispatch({
      type: INITIALIZE_APP_FAILED,
      payload: {
        error,
      },
    });

    throw error;
  }

  return dispatch({
    type: INITIALIZE_APP_SUCCESS,
  });
};

const changeCurrentSpace =
  ({ newSpace }) =>
  async (dispatch) => {
    // get access token for that space
    await dispatch(fetchNewTokenIfNeeded({ spaceId: newSpace.id }));

    dispatch({
      type: CHANGE_CURRENT_SPACE,
      payload: {
        newSpace,
      },
    });

    Analytics.track({
      event: CHANGE_SPACE,
      newSpace: newSpace.id,
      isInstructorInNewSpace: newSpace.isEducator,
    });

    // update the space in analytics
    Analytics.setClassRoom({
      activeClassroom: newSpace.id,
      isInstructor: newSpace.isEducator,
      accessingAsStaff: newSpace.isStaffOnly,
    });
  };

export default {
  initializeApp,
  changeCurrentSpace,
  fetchNewTokenIfNeeded,
};
